import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames/bind";
import Button from "../button";
import {
  getStoredConsent,
  setStoredConsent,
  loadGoogleAnalytics,
  disableGoogleAnalytics,
  COOKIE_CONSENT_REOPEN_EVENT,
} from "../../../utils/analytics-consent";
import styles from "./CookieConsentBanner.module.scss";

const cx = classNames.bind(styles);

// Internal legal route — content switches DE/EN based on the active UI
// language (see LegalPageLayout). Replaced the external marketing-site
// placeholder now that the real page is published on the platform.
const PRIVACY_POLICY_PATH = "/datenschutz";

/**
 * GDPR/TKG §165 cookie consent banner. Blocks GA4 analytics until the
 * visitor explicitly accepts. Persists the choice in localStorage and
 * exposes a small "Cookie settings" affordance so the choice can be
 * changed at any time later. Also re-opens on demand via the
 * COOKIE_CONSENT_REOPEN_EVENT (e.g. the footer's "Cookie settings" link).
 */
const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    setBannerOpen(getStoredConsent() === null);
  }, []);

  useEffect(() => {
    const reopen = () => setBannerOpen(true);
    window.addEventListener(COOKIE_CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_CONSENT_REOPEN_EVENT, reopen);
  }, []);

  const handleAccept = () => {
    setStoredConsent("accepted");
    loadGoogleAnalytics();
    setBannerOpen(false);
  };

  const handleReject = () => {
    setStoredConsent("rejected");
    disableGoogleAnalytics();
    setBannerOpen(false);
  };

  if (!bannerOpen) {
    return (
      <button
        type="button"
        className={cx("settingsLink")}
        onClick={() => setBannerOpen(true)}
      >
        {t("cookieConsent.settingsLink", "Cookie settings")}
      </button>
    );
  }

  return (
    <div className={cx("banner")} role="dialog" aria-live="polite" aria-label={t("cookieConsent.title", "Cookie settings")}>
      <p className={cx("message")}>
        {t(
          "cookieConsent.message",
          "We use cookies to understand how the platform is used (analytics). We only load these after you accept."
        )}{" "}
        <Link className={cx("link")} to={PRIVACY_POLICY_PATH} target="_blank" rel="noopener noreferrer">
          {t("cookieConsent.privacyLink", "Privacy policy")}
        </Link>
      </p>
      <div className={cx("actions")}>
        <Button variant="NORMAL" className={cx("actionBtn")} onClick={handleReject}>
          {t("cookieConsent.reject", "Reject")}
        </Button>
        <Button variant="SOLID" className={cx("actionBtn")} onClick={handleAccept}>
          {t("cookieConsent.accept", "Accept")}
        </Button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
